import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { statusInfoSquare } from '@harbor/elements/icons';
import StbMultiSelectGroupedWithSearch from '../security-toolbox-ui/StbMultiSelectGroupedWithSearch';
import StbButton from '../security-toolbox-ui/StbButton';

interface StbMultiSelectWithListItem {
  id: string | number;
  name: string;
  [key: string]: any;
}

interface ListWithGroupedMultiSelectProps {
  title: string;
  children: (item: StbMultiSelectWithListItem, index: number) => ReactNode;
  selectedItems: StbMultiSelectWithListItem[];
  optionItems: StbMultiSelectWithListItem[];
  typeLabel: string;
  loading: boolean;
  onClick: (selectedItems: StbMultiSelectWithListItem[]) => Promise<void> | void;
}

const getCategoryName = (item: any) => {
  return item?.category?.name ?? 'Uncategorized';
};

const ListWithGroupedMultiSelect = ({
                                      title,
                                      children,
                                      selectedItems,
                                      optionItems,
                                      typeLabel,
                                      loading,
                                      onClick,
                                    }: ListWithGroupedMultiSelectProps) => {
  const [showField, setShowField] = useState<boolean>(false);
  const [selected, setSelected] = useState<StbMultiSelectWithListItem[]>([]);
  const editAreaRef = useRef<HTMLDivElement | null>(null);

  const initialized = useMemo(() => selectedItems !== undefined, [selectedItems]);

  useEffect(() => {
    if (selectedItems) setSelected(selectedItems);
  }, [selectedItems]);

  const handleCancelClick = () => {
    setShowField(false);
    setSelected(selectedItems ? selectedItems : []);
  };

  const safeOptionItems = (optionItems ?? []).filter(Boolean);
  const safeSelectedItems = (selectedItems ?? []).filter(Boolean);
  const safeSelected = (selected ?? []).filter(Boolean);

  const groupedItems = useMemo(
    () =>
      safeOptionItems.map((item) => ({
        ...item,
        categoryName: getCategoryName(item),
      })),
    [safeOptionItems]
  );

  const groupedSelected = useMemo(
    () =>
      groupedItems.filter((item) =>
        safeSelected.some((selectedItem) => String(selectedItem.id) === String(item.id))
      ),
    [groupedItems, safeSelected]
  );

  const handleSaveClick = async () => {
    await onClick(safeSelected);
    setShowField(false);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h3>{title}</h3>
      </div>

      {!initialized && <HbrSpinner size="small" />}

      {initialized && !loading && (!safeSelectedItems || safeSelectedItems.length === 0) ? (
        <div className="bg-blue-100 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
          <HbrIcon asset={statusInfoSquare} sentiment="info" />
          No {typeLabel} found
        </div>
      ) : (
        <ul className="pl-5 mb-2">
          {safeSelectedItems.map((item, index) => children(item, index))}
        </ul>
      )}

      {showField ? (
        <div
          ref={editAreaRef}
          className="flex flex-col items-stretch gap-3 mb-4 pt-2 w-full min-w-0"
        >
          <StbMultiSelectGroupedWithSearch
            items={groupedItems}
            selected={groupedSelected}
            groupBy="categoryName"
            boundaryRef={editAreaRef}
            onChange={(nextSelected) =>
              setSelected(
                [...(nextSelected ?? [])].map(({ categoryName, ...rest }: any) => rest)
              )
            }
            className="w-full min-w-0"
          />

          <div className="flex flex-wrap gap-2">
            <StbButton
              role="save"
              size="small"
              onClick={handleSaveClick}
              disabled={loading}
            >
              {loading ? 'Updating...' : `Update ${typeLabel}`}
            </StbButton>

            <StbButton
              role="cancel"
              size="small"
              onClick={handleCancelClick}
              disabled={loading}
            >
              Close
            </StbButton>
          </div>
        </div>
      ) : (
        <StbButton
          role="update"
          size="small"
          onClick={() => setShowField(true)}
          className="mb-8 flex-start pt-2"
        >
          Edit {typeLabel}
        </StbButton>
      )}
    </>
  );
};

export default ListWithGroupedMultiSelect;